// src/pages/customer/customerDashboard/Profile.jsx
import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Card, Form, Tab, Nav } from "react-bootstrap";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCamera,
  FiSave,
  FiLock,
  FiBell,
  FiEye,
  FiEyeOff,
  FiHome,
} from "react-icons/fi";
import axios from "axios";
import CustomerMasterLayout from "../../layouts/CustomerMasterLayout";
import Button from "../../components/general/button/Button";
import { ProfilePageSkeleton } from "../../components/general/skeleton/SkeletonBlock";
import "../../assets/css/customer/customerDashboard/profile/Profile.css";
import Swal from "sweetalert2";
import MyContext from "../../context/MyContext";

// ── inline error styles — jab tak Profile.css mein dedicated error
// classes nahi ban jatin, red border/message ye reuse karte hain ──
const errBorder = { borderColor: "#dc3545" };
const errText = {
  color: "#dc3545",
  fontSize: 12,
  marginTop: 4,
  marginBottom: 0,
};

// ─────────────────────────────────────────────────────────────────────────────
const Profile = () => {
  const { baseUrl, baseUrlForFiles, authToken, regExpChecker } =
    useContext(MyContext);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");

  // ── Personal Info ──
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [image, setImage] = useState("");
  const [address, setAddress] = useState(null);
  const [city, setCity] = useState("");
  const [customerProfile, setCustomerProfile] = useState("");

  // ── NEW: Personal Info field-level errors ──
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [addressError, setAddressError] = useState("");
  const [cityError, setCityError] = useState("");

  // ── Action loading states ──
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);

  // ── Security ──
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // ── NEW: Security field-level errors ──
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ── Notifications ──
  const [emailNotification, setEmailNotification] = useState(true);
  const [inAppNotification, setInAppNotification] = useState(true);

  const viewCustomerProfile = () => {
    axios
      .get(`${baseUrl}/customer/profile/viewCustomerProfile`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          const cust = response.data.customer;
          setCustomerProfile(cust);
          setName(cust.name || "");
          setEmail(cust.email || "");
          setPhone(cust.phone || "");
          setAddress(cust.address || "");
          setCity(cust.city || "");
          setImage(cust.image || "");
          setEmailNotification(cust.emailNotification == 1);
          setInAppNotification(cust.inAppNotification == 1);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Profile cannot load .",
        });
      });
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      return;
    }
    viewCustomerProfile();
  }, [authToken]);

  // ── NEW: Personal Info validation ──
  const validatePersonalInfo = () => {
    let valid = true;

    const nameErr = regExpChecker("username", name);
    if (!name) {
      setNameError("Name is required");
      valid = false;
    } else if (nameErr) {
      setNameError(nameErr);
      valid = false;
    } else setNameError("");

    const emailErr = regExpChecker("email", email);
    if (!email) {
      setEmailError("Email is required");
      valid = false;
    } else if (emailErr) {
      setEmailError(emailErr);
      valid = false;
    } else setEmailError("");

    if (phone) {
      const phoneErr = regExpChecker("phoneNumber", phone);
      if (phoneErr) {
        setPhoneError(phoneErr);
        valid = false;
      } else setPhoneError("");
    } else setPhoneError("");

    if (!address?.trim?.()) {
      setAddressError("Address is required");
      valid = false;
    } else setAddressError("");

    if (!city?.trim()) {
      setCityError("City is required");
      valid = false;
    } else setCityError("");

    return valid;
  };

  const updateCustomerProfile = () => {
    setSavingProfile(true);
    const formData = new FormData();
    formData.append("name", name);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("address", address);
    formData.append("city", city);
    axios
      .post(`${baseUrl}/customer/profile/updateCustomerProfile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setSavingProfile(false);
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Profile updated successfully!",
          });
          viewCustomerProfile();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update profile.",
          });
        }
      })
      .catch((error) => {
        setSavingProfile(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update profile.",
        });
      });
  };

  const uploadProfileImage = (file) => {
    if (!file) return;
    setUploadingImage(true);
    const formData = new FormData();
    formData.append("image", file);
    axios
      .post(`${baseUrl}/customer/profile/updateCustomerProfile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setUploadingImage(false);
        if (response.data.success == true) {
          viewCustomerProfile();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update photo.",
          });
        }
      })
      .catch((error) => {
        setUploadingImage(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update photo.",
        });
      });
  };

  // ── NEW: Password validation ──
  const validatePasswordChange = () => {
    let valid = true;

    if (!currentPassword) {
      setCurrentPasswordError("Please enter your current password");
      valid = false;
    } else setCurrentPasswordError("");

    const newPassErr = regExpChecker("password", newPassword);
    if (!newPassword) {
      setNewPasswordError("New password is required");
      valid = false;
    } else if (newPassword.length < 8) {
      setNewPasswordError("New password must be at least 8 characters");
      valid = false;
    } else if (newPassErr) {
      setNewPasswordError(newPassErr);
      valid = false;
    } else setNewPasswordError("");

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your new password");
      valid = false;
    } else if (confirmPassword !== newPassword) {
      setConfirmPasswordError("Passwords do not match");
      valid = false;
    } else setConfirmPasswordError("");

    return valid;
  };

  const changeCustomerPassword = () => {
    setChangingPassword(true);
    const formData = new FormData();
    formData.append("currentPassword", currentPassword);
    formData.append("newPassword", newPassword);
    axios
      .post(`${baseUrl}/customer/profile/changeCustomerPassword`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setChangingPassword(false);
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Password changed successfully!",
          });
          setCurrentPassword("");
          setNewPassword("");
          setConfirmPassword("");
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update Password",
          });
        }
      })
      .catch((error) => {
        setChangingPassword(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update profile.",
        });
      });
  };

  const changeNotificationSetting = () => {
    setSavingPreferences(true);
    const formData = new FormData();
    formData.append("emailNotification", emailNotification ? 1 : 0);
    formData.append("inAppNotification", inAppNotification ? 1 : 0);

    axios
      .post(`${baseUrl}/customer/profile/changeNotificationSetting`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        setSavingPreferences(false);
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Notification settings updated!",
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to update notification settings",
          });
        }
      })
      .catch((error) => {
        setSavingPreferences(false);
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update notification settings.",
        });
      });
  };

  const togglePass = (key) => {
    if (key === "current") setShowCurrentPassword((p) => !p);
    else if (key === "new") setShowNewPassword((p) => !p);
    else if (key === "confirm") setShowConfirmPassword((p) => !p);
  };

  if (loading)
    return (
      <CustomerMasterLayout>
        <ProfilePageSkeleton />
      </CustomerMasterLayout>
    );

  return (
    <CustomerMasterLayout>
      <Container fluid className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <div className="profile-header-left">
            <div className="profile-icon-wrapper">
              <FiUser size={30} />
            </div>
            <div>
              <h2 className="profile-page-title">My Profile</h2>
              <p className="profile-subtitle">
                Manage your personal information
              </p>
            </div>
          </div>
        </div>

        <Row className="g-4">
          {/* ── Left Sidebar ── */}
          <Col xs={12} lg={4} xl={3}>
            <Card className="profile-sidebar-card">
              <Card.Body className="text-center">
                <div className="profile-image-wrapper">
                  <img
                    src={`${baseUrlForFiles}/${image}`}
                    alt="Profile"
                    className="profile-image"
                  />
                  <label className="profile-upload-btn" title="Change photo">
                    <FiCamera size={18} />
                    <input
                      type="file"
                      hidden
                      onChange={(e) => uploadProfileImage(e.target.files[0])}
                      accept="image/*"
                      disabled={uploadingImage}
                    />
                  </label>
                  {uploadingImage && (
                    <span className="profile-image-uploading">
                      Uploading...
                    </span>
                  )}
                </div>
                <h5 className="profile-name">{name}</h5>
                <p className="profile-email">{email}</p>
                <p className="profile-phone">{phone}</p>
              </Card.Body>
            </Card>
          </Col>

          {/* ── Right Content ── */}
          <Col xs={12} lg={8} xl={9}>
            <Card className="profile-content-card">
              <Card.Body>
                <Tab.Container
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                >
                  {/* Tabs Nav */}
                  <Nav variant="pills" className="profile-tabs mb-3">
                    <Nav.Item>
                      <Nav.Link eventKey="personal">
                        <FiUser className="me-2" />
                        Personal
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="security">
                        <FiLock className="me-2" />
                        Security
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="notifications">
                        <FiBell className="me-2" />
                        Notifications
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <Tab.Content className="profile-tab-content">
                    {/* ══ PERSONAL TAB ══════════════════════════════════════════ */}
                    <Tab.Pane eventKey="personal">
                      <div className="form-section-header">
                        <h5 className="form-section-title">
                          Personal Information
                        </h5>
                        <Button
                          variant="primary"
                          icon={<FiSave size={16} />}
                          disabled={savingProfile}
                          onClick={() => {
                            if (validatePersonalInfo()) updateCustomerProfile();
                          }}
                        >
                          {savingProfile ? "Saving..." : "Save Changes"}
                        </Button>
                      </div>

                      <Form
                        id="personal-info-form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (validatePersonalInfo()) updateCustomerProfile();
                        }}
                      >
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Label className="form-label">
                              <FiUser className="me-2" />
                              Name
                            </Form.Label>
                            <Form.Control
                              value={name}
                              style={nameError ? errBorder : undefined}
                              onChange={(e) => {
                                setName(e.target.value);
                                setNameError(
                                  e.target.value
                                    ? regExpChecker(
                                        "username",
                                        e.target.value,
                                      ) || ""
                                    : "Name is required",
                                );
                              }}
                              placeholder="Name"
                              className="form-input"
                            />
                            {nameError && <p style={errText}>{nameError}</p>}
                          </Col>
                          <Col md={6}>
                            <Form.Label className="form-label">
                              <FiMail className="me-2" />
                              Email
                            </Form.Label>
                            <Form.Control
                              type="email"
                              value={email}
                              style={emailError ? errBorder : undefined}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailError(
                                  e.target.value
                                    ? regExpChecker("email", e.target.value) ||
                                        ""
                                    : "Email is required",
                                );
                              }}
                              placeholder="Email"
                              className="form-input"
                            />
                            {emailError && <p style={errText}>{emailError}</p>}
                          </Col>
                          <Col md={6}>
                            <Form.Label className="form-label">
                              <FiPhone className="me-2" />
                              Phone
                            </Form.Label>
                            <Form.Control
                              value={phone}
                              style={phoneError ? errBorder : undefined}
                              onChange={(e) => {
                                setPhone(e.target.value);
                                setPhoneError(
                                  e.target.value
                                    ? regExpChecker(
                                        "phoneNumber",
                                        e.target.value,
                                      ) || ""
                                    : "",
                                );
                              }}
                              placeholder="0300-1234567"
                              className="form-input"
                            />
                            {phoneError && <p style={errText}>{phoneError}</p>}
                          </Col>
                          <Col md={6}>
                            <Form.Label className="form-label">
                              <FiHome className="me-2" />
                              Address
                            </Form.Label>
                            <Form.Control
                              value={address || ""}
                              style={addressError ? errBorder : undefined}
                              onChange={(e) => {
                                setAddress(e.target.value);
                                setAddressError(
                                  e.target.value.trim()
                                    ? ""
                                    : "Address is required",
                                );
                              }}
                              placeholder="House #, Street, Area"
                              className="form-input"
                            />
                            {addressError && (
                              <p style={errText}>{addressError}</p>
                            )}
                          </Col>
                          <Col md={6}>
                            <Form.Label className="form-label">
                              <FiMapPin className="me-2" />
                              City
                            </Form.Label>
                            <Form.Control
                              value={city}
                              style={cityError ? errBorder : undefined}
                              onChange={(e) => {
                                setCity(e.target.value);
                                setCityError(
                                  e.target.value.trim()
                                    ? ""
                                    : "City is required",
                                );
                              }}
                              placeholder="City"
                              className="form-input"
                            />
                            {cityError && <p style={errText}>{cityError}</p>}
                          </Col>

                          {/* Address Preview */}
                          {address && (
                            <Col xs={12}>
                              <div className="profile-address-preview">
                                <div className="address-preview-icon">
                                  <FiMapPin size={20} />
                                </div>
                                <div className="address-preview-text">
                                  <p className="address-preview-label">
                                    Delivery will be sent to:
                                  </p>
                                  <p className="address-preview-full">
                                    {address}
                                  </p>
                                </div>
                              </div>
                            </Col>
                          )}
                        </Row>
                      </Form>
                    </Tab.Pane>

                    {/* ══ SECURITY TAB ══════════════════════════════════════════ */}
                    <Tab.Pane eventKey="security">
                      <h5 className="form-section-title">Change Password</h5>

                      {/* Security Tips */}
                      <div className="profile-security-tips mb-4">
                        <p className="security-tip-title">
                          🔒 Password Requirements:
                        </p>
                        <ul className="security-tip-list">
                          <li
                            className={newPassword.length >= 8 ? "tip-ok" : ""}
                          >
                            At least 8 characters
                          </li>
                          <li
                            className={
                              /[A-Z]/.test(newPassword) ? "tip-ok" : ""
                            }
                          >
                            At least one uppercase letter
                          </li>
                          <li
                            className={
                              /[0-9]/.test(newPassword) ? "tip-ok" : ""
                            }
                          >
                            At least one number
                          </li>
                          <li
                            className={
                              /[^a-zA-Z0-9]/.test(newPassword) ? "tip-ok" : ""
                            }
                          >
                            At least one special character
                          </li>
                        </ul>
                      </div>

                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (validatePasswordChange())
                            changeCustomerPassword();
                        }}
                      >
                        <Row className="g-3">
                          {[
                            {
                              key: "current",
                              label: "Current Password",
                              value: currentPassword,
                              setter: setCurrentPassword,
                              error: currentPasswordError,
                            },
                            {
                              key: "new",
                              label: "New Password",
                              value: newPassword,
                              setter: setNewPassword,
                              error: newPasswordError,
                            },
                            {
                              key: "confirm",
                              label: "Confirm Password",
                              value: confirmPassword,
                              setter: setConfirmPassword,
                              error: confirmPasswordError,
                            },
                          ].map(({ key, label, value, setter, error }) => (
                            <Col xs={12} md={6} key={key}>
                              <Form.Label className="form-label">
                                <FiLock className="me-2" />
                                {label}
                              </Form.Label>
                              <div className="profile-password-wrapper">
                                <Form.Control
                                  type={
                                    (key === "current" &&
                                      showCurrentPassword) ||
                                    (key === "new" && showNewPassword) ||
                                    (key === "confirm" && showConfirmPassword)
                                      ? "text"
                                      : "password"
                                  }
                                  value={value}
                                  style={error ? errBorder : undefined}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setter(v);

                                    if (key === "current") {
                                      setCurrentPasswordError(
                                        v
                                          ? ""
                                          : "Please enter your current password",
                                      );
                                    } else if (key === "new") {
                                      if (!v)
                                        setNewPasswordError(
                                          "New password is required",
                                        );
                                      else if (v.length < 8)
                                        setNewPasswordError(
                                          "New password must be at least 8 characters",
                                        );
                                      else
                                        setNewPasswordError(
                                          regExpChecker("password", v) || "",
                                        );
                                      if (confirmPassword) {
                                        setConfirmPasswordError(
                                          confirmPassword === v
                                            ? ""
                                            : "Passwords do not match",
                                        );
                                      }
                                    } else if (key === "confirm") {
                                      if (!v)
                                        setConfirmPasswordError(
                                          "Please confirm your new password",
                                        );
                                      else
                                        setConfirmPasswordError(
                                          v === newPassword
                                            ? ""
                                            : "Passwords do not match",
                                        );
                                    }
                                  }}
                                  placeholder={label}
                                  className="form-input profile-password-input"
                                />
                                <button
                                  type="button"
                                  className="profile-password-toggle"
                                  onClick={() => togglePass(key)}
                                >
                                  {(key === "current" && showCurrentPassword) ||
                                  (key === "new" && showNewPassword) ||
                                  (key === "confirm" && showConfirmPassword) ? (
                                    <FiEyeOff size={16} />
                                  ) : (
                                    <FiEye size={16} />
                                  )}
                                </button>
                              </div>
                              {error && <p style={errText}>{error}</p>}

                              {/* Strength bar for new password */}
                              {key === "new" && newPassword.length > 0 && (
                                <div className="profile-password-strength mt-2">
                                  <div
                                    className="strength-bar"
                                    style={{
                                      width: `${Math.min(100, (newPassword.length / 12) * 100)}%`,
                                      background:
                                        newPassword.length < 6
                                          ? "#dc3545"
                                          : newPassword.length < 10
                                            ? "#ffc107"
                                            : "var(--gradient-primary)",
                                    }}
                                  />
                                  <span className="strength-label">
                                    {newPassword.length < 6
                                      ? "Weak"
                                      : newPassword.length < 10
                                        ? "Medium"
                                        : "Strong"}
                                  </span>
                                </div>
                              )}

                              {/* Match indicator */}
                              {key === "confirm" &&
                                confirmPassword.length > 0 && (
                                  <p
                                    className={`profile-password-match mt-1 ${newPassword === confirmPassword ? "match-ok" : "match-fail"}`}
                                  >
                                    {newPassword === confirmPassword
                                      ? "✓ Passwords match"
                                      : "✗ Passwords do not match"}
                                  </p>
                                )}
                            </Col>
                          ))}

                          <Col xs={12}>
                            <Button
                              type="submit"
                              variant="primary"
                              icon={<FiLock size={16} />}
                              disabled={changingPassword}
                            >
                              {changingPassword
                                ? "Changing..."
                                : "Change Password"}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </Tab.Pane>

                    {/* ══ NOTIFICATIONS TAB ═════════════════════════════════════ */}
                    <Tab.Pane eventKey="notifications">
                      <h5 className="form-section-title">
                        Notification Preferences
                      </h5>
                      <p className="form-section-subtitle">
                        Choose how you want to be notified about your account
                        and orders.
                      </p>

                      <div className="profile-notif-list">
                        <div className="profile-notif-item">
                          <div className="profile-notif-info">
                            <p className="profile-notif-label">
                              Email Notifications
                            </p>

                            <p className="profile-notif-desc">
                              Receive updates and alerts via email
                            </p>
                          </div>

                          <Form.Check
                            type="switch"
                            className="custom-switch"
                            checked={emailNotification}
                            onChange={() => {
                              setEmailNotification(!emailNotification);
                            }}
                          />
                        </div>

                        <div className="profile-notif-item">
                          <div className="profile-notif-info">
                            <p className="profile-notif-label">In App</p>

                            <p className="profile-notif-desc">
                              Get In app notifications
                            </p>
                          </div>

                          <Form.Check
                            type="switch"
                            className="custom-switch"
                            checked={inAppNotification}
                            onChange={() => {
                              setInAppNotification(!inAppNotification);
                            }}
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <Button
                          variant="primary"
                          icon={<FiSave size={16} />}
                          onClick={() => changeNotificationSetting()}
                          disabled={savingPreferences}
                        >
                          {savingPreferences ? "Saving..." : "Save Preferences"}
                        </Button>
                      </div>
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </CustomerMasterLayout>
  );
};

export default Profile;
